import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, FormControlLabel, Checkbox, Link, TableSortLabel, Tooltip
} from '@mui/material';
import { makeRequest } from '../../api';
import { OverRow, StatPair } from "../../interfaces/OverRow";
import { MomentumDialog } from "../../components/MomentumDialog";

const pair = (p?: StatPair | null) => (p && (p.h != null || p.a != null) ? `${p.h ?? '-'}-${p.a ?? '-'}` : '—');
const pct = (p: number | null) => (p != null ? `${Math.round(p * 100)}%` : '—');

// Color/flag por underEdge (valor del under = "no pasa nada").
const edgeColor = (e: number | null): string | undefined => {
    if (e == null) return undefined;
    if (e >= 0.10) return '#b9f6ca';
    if (e >= 0.05) return '#dcedc8';
    if (e >= 0) return '#fff9c4';
    return '#ffcdd2';
};
const edgeFlag = (e: number | null) => (e == null ? '' : e >= 0.10 ? '🔥' : e >= 0.05 ? '🟢' : e >= 0 ? '🟡' : '🔴');
const edgeText = (e: number | null) => (e != null ? `${e >= 0 ? '+' : ''}${(e * 100).toFixed(1)}%` : '—');

// Para el under, el momentum es informativo igual (un partido con presión
// subiendo lo VUELVE menos seguro como "no pasa nada"). Misma celda visual.
const SLOPE_TH = 0.5;
const momentumCell = (r: OverRow): React.ReactNode => {
    const m = r.momentum;
    if (!m || m.slopeDatt == null) return '—';
    const s = m.slopeDatt;
    const arrow = s > SLOPE_TH ? '↑' : s < -SLOPE_TH ? '↓' : '→';
    const arrowColor = s > SLOPE_TH ? '#2e7d32' : s < -SLOPE_TH ? '#c62828' : '#90a4ae';
    const domStyle: React.CSSProperties = m.dominant === 'home' ? { color: '#1976d2', fontWeight: 600 }
        : m.dominant === 'away' ? { color: '#e64a19', fontWeight: 600 }
        : { color: '#90a4ae' };
    const domLetter = m.dominant === 'home' ? 'H' : m.dominant === 'away' ? 'A' : '=';
    return (
        <span>
            <span style={{ color: arrowColor, fontWeight: 600 }}>{arrow} {s.toFixed(1)}</span>
            <span style={{ marginLeft: 6, ...domStyle }}>{domLetter}</span>
        </span>
    );
};

type Align = 'left' | 'center';
interface Column {
    key: string;
    label: string;
    align: Align;
    sortable: boolean;
    tooltip?: string;
    render: (r: OverRow) => React.ReactNode;
}

const columns: Column[] = [
    { key: 'flag', label: '', align: 'center', sortable: false, tooltip: 'Valor del under según underEdge: 🔥 ≥10% · 🟢 ≥5% · 🟡 ≥0% · 🔴 <0%', render: (r) => edgeFlag(r.underEdge) },
    { key: 'league', label: 'Liga', align: 'left', sortable: true, render: (r) => r.league },
    { key: 'match', label: 'Partido', align: 'left', sortable: true, render: (r) => r.competitors?.join(' vs ') || r.matchID },
    { key: 'minute', label: 'Min', align: 'center', sortable: true, tooltip: 'Minuto actual (1T/2T)', render: (r) => `${r.minute}'${r.ht ? ' (2T)' : ' (1T)'}` },
    { key: 'totGoals', label: 'Marcador', align: 'center', sortable: true, render: (r) => `${r.score[0]}-${r.score[1]}` },
    { key: 'oddsUnder', label: 'Cuota under', align: 'center', sortable: true, tooltip: 'Cuota de "Menos de (goles_actuales + 0.5)" = que NO haya más goles. "—" = Novibet no ofrece esa línea exacta.', render: (r) => r.oddsUnder ?? '—' },
    { key: 'probUnder', label: 'Prob under (modelo)', align: 'center', sortable: true, tooltip: 'Probabilidad del modelo de que NO haya más goles = e^(−λ) = 1 − Prob(gol más).', render: (r) => <strong>{pct(r.probUnder)}</strong> },
    { key: 'underProbMarket', label: 'Prob mercado', align: 'center', sortable: true, tooltip: 'Probabilidad implícita en la cuota under ajustada por margen del bookie: 0.915 / cuota (≈ remueve ~8.5% de overround).', render: (r) => pct(r.underProbMarket) },
    { key: 'underEdge', label: 'Under Edge', align: 'center', sortable: true, tooltip: 'Ventaja del under = Prob under (modelo) − Prob mercado. Positivo = el mercado subestima que "no pasa nada".', render: (r) => <strong>{edgeText(r.underEdge)}</strong> },
    { key: 'timeRemaining', label: 'T. restante', align: 'center', sortable: true, tooltip: 'Minutos estimados hasta el final (FT estimado − minuto actual). Menos tiempo ⇒ más probable que no pase nada.', render: (r) => (r.timeRemaining != null ? `${r.timeRemaining}'` : '—') },
    { key: 'datt', label: 'Ataq. pelig. (últ)', align: 'center', sortable: true, tooltip: 'Ataques peligrosos (ambos equipos) en la ventana reciente. BAJO es bueno para el under.', render: (r) => (r.intensity ? r.intensity.dattLast : '—') },
    { key: 'shots', label: 'Tiros (últ)', align: 'center', sortable: true, tooltip: 'Tiros TOTALES (al arco + fuera, ambos equipos sumados) en la ventana reciente. BAJO es bueno para el under (poca actividad ofensiva).', render: (r) => (r.intensity ? r.intensity.shotsLast : '—') },
    { key: 'momentum', label: 'Mom', align: 'center', sortable: true, tooltip: 'Momentum: pendiente de intensidad (últimos 2 min vs 4 anteriores). Para el under: ↑ es MALO (presión subiendo aumenta riesgo de gol). Letra = equipo dominante. Persistido en alertas para backtest.', render: (r) => momentumCell(r) },
    { key: 'poss', label: 'Posesión', align: 'center', sortable: true, render: (r) => pair(r.stats?.poss) },
    { key: 'link', label: 'Link', align: 'center', sortable: false, render: (r) => (r.link ? <Link href={r.link} target="_blank" rel="noopener">ver</Link> : '—') },
];

const accessors: Record<string, (r: OverRow) => number | string | null> = {
    league: (r) => r.league,
    match: (r) => (r.competitors || []).join(' '),
    minute: (r) => r.minute,
    totGoals: (r) => r.totGoals,
    oddsUnder: (r) => r.oddsUnder,
    probUnder: (r) => r.probUnder,
    underProbMarket: (r) => r.underProbMarket,
    underEdge: (r) => r.underEdge,
    timeRemaining: (r) => r.timeRemaining,
    datt: (r) => (r.intensity ? r.intensity.dattLast : null),
    shots: (r) => (r.intensity ? r.intensity.shotsLast : null),
    momentum: (r) => r.momentum?.slopeDatt ?? null,
    poss: (r) => r.stats?.poss?.h ?? null,
};

type Order = 'asc' | 'desc';

const UnderTable = () => {
    const [data, setData] = useState<OverRow[]>([]);
    const [openMatchID, setOpenMatchID] = useState<string | null>(null);
    const [onlyWithEdge, setOnlyWithEdge] = useState<boolean>(false);
    const [updatedAt, setUpdatedAt] = useState<string>('');
    const [orderBy, setOrderBy] = useState<string>('underEdge');
    const [order, setOrder] = useState<Order>('desc');

    const fetchData = useCallback(async () => {
        try {
            const res: any = await makeRequest('api/over/ranking', { method: 'get' });
            if (res.status === 200) {
                setData(res.data as OverRow[]);
                setUpdatedAt(new Date().toLocaleTimeString());
            }
        } catch (e) {
            // makeRequest ya loggea
        }
    }, []);

    useEffect(() => {
        fetchData();
        const id = setInterval(fetchData, 30000);
        return () => clearInterval(id);
    }, [fetchData]);

    const handleSort = (key: string) => {
        if (orderBy === key) setOrder(order === 'asc' ? 'desc' : 'asc');
        else { setOrderBy(key); setOrder('desc'); }
    };

    const rows = useMemo(() => {
        const filtered = onlyWithEdge ? data.filter((r) => r.underEdge != null) : data;
        const acc = accessors[orderBy];
        if (!acc) return filtered;
        return [...filtered].sort((a, b) => {
            const va = acc(a);
            const vb = acc(b);
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
            return order === 'asc' ? cmp : -cmp;
        });
    }, [data, onlyWithEdge, orderBy, order]);

    return (
        <div>
            <Paper variant="outlined" style={{ padding: '10px 14px', margin: '8px 0', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-line', background: '#fafafa' }}>
                {[
                    'Under inverso ("no pasará nada") — mismo modelo, complemento:',
                    '  P(no más goles) = e^(−λ) = 1 − P(gol más)',
                    '  underEdge = P(no más goles) − P(mercado)        P(mercado) = 0.915 / cuota under   (≈ remueve ~8.5% de margen del bookie)',
                    '  Cuota under = "Menos de (goles_actuales + 0.5)"',
                ].join('\n')}
            </Paper>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '10px 0', flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" onClick={fetchData}>Refresh</Button>
                <FormControlLabel
                    control={<Checkbox checked={onlyWithEdge} onChange={(e) => setOnlyWithEdge(e.target.checked)} />}
                    label="Solo con cuota under"
                />
                <span style={{ color: '#888' }}>
                    {data.length} partidos · {data.filter((r) => r.underEdge != null).length} con cuota under · act. {updatedAt}
                </span>
                <span style={{ color: '#888' }}>🔥 ≥10% · 🟢 ≥5% · 🟡 ≥0% · 🔴 &lt;0%</span>
            </div>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((c) => {
                                const inner = c.sortable ? (
                                    <TableSortLabel
                                        active={orderBy === c.key}
                                        direction={orderBy === c.key ? order : 'desc'}
                                        onClick={() => handleSort(c.key)}
                                    >
                                        {c.label}
                                    </TableSortLabel>
                                ) : <span>{c.label}</span>;
                                return (
                                    <TableCell key={c.key} align={c.align} sortDirection={orderBy === c.key ? order : false}>
                                        {c.tooltip
                                            ? <Tooltip title={c.tooltip} arrow placement="top"><span>{inner}</span></Tooltip>
                                            : inner}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow
                                key={r.matchID}
                                hover
                                style={{ backgroundColor: edgeColor(r.underEdge), cursor: 'pointer' }}
                                onClick={(e) => {
                                    if ((e.target as HTMLElement).closest('a')) return;
                                    setOpenMatchID(r.matchID);
                                }}
                            >
                                {columns.map((c) => (
                                    <TableCell key={c.key} align={c.align}>{c.render(r)}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <MomentumDialog matchID={openMatchID} onClose={() => setOpenMatchID(null)} />
        </div>
    );
};

export { UnderTable };
